import { useEffect, useState } from "react";

function scanLocalStorageCache() {
  let ts = null, used = false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("cache:")) continue;
      used = true;
      try {
        const p = JSON.parse(localStorage.getItem(key));
        if (p?.ts && (!ts || p.ts > ts)) ts = p.ts;
      } catch {}
    }
  } catch {}
  return { used, ts };
}

function detectSlowConnection() {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  if (c.saveData) return true;
  if (c.effectiveType === 'slow-2g' || c.effectiveType === '2g') return true;
  if (typeof c.downlink === 'number' && c.downlink > 0 && c.downlink < 0.5) return true;
  if (typeof c.rtt === 'number' && c.rtt > 1000) return true;
  return false;
}

export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [backendDown, setBackendDown] = useState(false);
  const [slowNet, setSlowNet] = useState(() => detectSlowConnection());

  // Инициализируем сразу из localStorage — на случай если компонент монтируется
  // когда офлайн уже наступил (например, переход между маршрутами пока нет сети)
  const [cacheUsed, setCacheUsed] = useState(() => {
    if (navigator.onLine) return false;
    return scanLocalStorageCache().used;
  });
  const [cacheTs, setCacheTs] = useState(() => {
    if (navigator.onLine) return null;
    return scanLocalStorageCache().ts;
  });

  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      setCacheTs(null);
      setCacheUsed(false);
    };
    const goOffline = () => {
      setIsOnline(false);
      const { used, ts } = scanLocalStorageCache();
      if (used) {
        setCacheUsed(true);
        if (ts) setCacheTs(ts);
      }
    };
    const onCacheFallback = (e) => {
      setCacheUsed(true);
      if (e.detail?.ts) setCacheTs(e.detail.ts);
    };
    const onBackendDown = () => setBackendDown(true);
    const onBackendUp = () => setBackendDown(false);

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const onConnChange = () => setSlowNet(detectSlowConnection());

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    window.addEventListener("audassist:offline-fallback", onCacheFallback);
    window.addEventListener("audassist:backend-down", onBackendDown);
    window.addEventListener("audassist:backend-up", onBackendUp);
    conn?.addEventListener?.("change", onConnChange);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("audassist:offline-fallback", onCacheFallback);
      window.removeEventListener("audassist:backend-down", onBackendDown);
      window.removeEventListener("audassist:backend-up", onBackendUp);
      conn?.removeEventListener?.("change", onConnChange);
    };
  }, []);

  return { isOnline, cacheTs, cacheUsed, backendDown, slowNet };
}

export function formatCacheTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return time;
  if (d.toDateString() === yesterday.toDateString()) return `вчера ${time}`;
  return `${d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" })} ${time}`;
}
